import { generateTooltipTextForConnector, getFlowConnectorConfig } from '../../utility/helpers';
import { JourneyCanvasState } from '../../utility/TypeDefs';
import { ExternalEventConfirmationSavePayload, ShowExternalEventConfirmationPayload } from '../TypeDefs';

/**
 * @method showExternalEventConfirmationModal
 * @description Shows the external event confirmation modal.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {ShowExternalEventConfirmationPayload} payload - The payload containing the nodeId.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const showExternalEventConfirmationModal = (state: JourneyCanvasState, { toNodeId, fromNodeId }: ShowExternalEventConfirmationPayload): JourneyCanvasState => ({
  ...state,
  showExternalEventConfirmationModal: true,
  externalEventConfirmationData: {
    toNodeId,
    fromNodeId,
    external: state.flowItemDataMap[toNodeId].external[`${fromNodeId}_${toNodeId}`]
  },
});

/**
 * @method hideExternalEventConfirmationModal
 * @description Hides the external event confirmation modal.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const hideExternalEventConfirmationModal = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  showExternalEventConfirmationModal: false,
});

/**
 * @method externalEventConfirmationSave
 * @description Saves the external value and updates the state of the journey canvas.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {ExternalEventConfirmationSavePayload} payload - The payload containing the external value and nodeId.
 * @returns {JourneyCanvasState} The updated state of the journey canvas.
 */
export const externalEventConfirmationSave = (state: JourneyCanvasState, { external, toNodeId, fromNodeId }: ExternalEventConfirmationSavePayload): JourneyCanvasState => {
  const connector = state.flowConnectors.find((c) => c.toNodeId === toNodeId && c.fromNodeId === fromNodeId);
  if (!connector) {
    return state;
  }

  connector.tooltip = generateTooltipTextForConnector(connector.text ?? '', !!state.flowItemDataMap[connector.fromNodeId]?.isExternal, external);

  connector.config = getFlowConnectorConfig({
    hasCondExpr: !!state.flowItemDataMap[toNodeId].condExpr[fromNodeId]?.rawExpression,
    mep: !!state.flowItemDataMap[toNodeId]?.isExternal,
    external: !!external
  });

  const updatedExternalObj = { ...state.flowItemDataMap[toNodeId]?.external };
  updatedExternalObj[`${fromNodeId}_${toNodeId}`] = external;

  return {
    ...state,
    showExternalEventConfirmationModal: false,
    flowItemDataMap: {
      ...state.flowItemDataMap,
      [toNodeId]: {
        ...state.flowItemDataMap[toNodeId],
        external: updatedExternalObj
      }
    },
    flowConnectors: [...state.flowConnectors]
  };
};
